import React, { useEffect, useMemo, useRef, useState } from 'react'
import moment from 'moment'
import DiaryDatePicker from '../../../../../Diary/DiaryDatePicker'
import ShiftTimePicker from '../../../../../Diary/ShiftTimePicker'
import StaffPicker from '../../../../../Diary/StaffPicker'
import { hapticTick } from '../../../../../../_utils/haptics'
import { freeDays, timeLabel, sortTimes, toggleTime, pickTimes, hasTime, freeAt, daysFor, teamIn, myStaffId, ownTimesOnly, startsOnOwn } from '../../data/freeSlots'
import { dayLabel, postingDay, isoDay, parseIso, minutesOf, isoIn } from '../../data/dates'
import { CalendarIcon, CloseIcon } from '../icons'

// The diary's free days, read once and shared (data/freeSlots.js)
export const useFreeDays = () => {
  const [state, setState] = useState({ days: null, failed: false })
  useEffect(() => {
    let alive = true
    freeDays().then((days) => { if (alive) setState({ days: days || [], failed: !days }) })
    return () => { alive = false }
  }, [])
  return state
}

// Who's free across the days, and who this is: a self-employed stylist only
// ever has their own; anyone else can pick when two or more are free.
// `first` is whose times a new post starts with (null for everyone's).
export const useWhose = (days) => {
  const team = useMemo(() => teamIn(days), [days])
  const own = ownTimesOnly()
  const me = myStaffId()
  const self = team.find(t => t.id === me) || null
  return { team, own, me, self, canChoose: !own && team.length > 1, first: startsOnOwn() && self ? self : null }
}

// The post's stylist, or none (everyone's times)
export const whoPatch = (member) => (member ? { staffId: member.id, staffName: member.firstName } : { staffId: undefined, staffName: undefined })

// "Whose free times": everyone, then each stylist free this week, with
// search (the diary's own stylist picker)
export const WhosePicker = ({ team, me, value, extra = null, onChange }) => {
  const list = [{ id: 'all', name: 'Everyone' }, ...team.map(t => ({ id: t.id, name: t.id === me ? `${t.name} (you)` : t.name }))]
  if (extra && extra.id && !list.some(x => x.id === extra.id)) list.push(extra)
  return (
    <div className="sx-who">
      <StaffPicker label="Whose free times" placeholder="Everyone" staffList={list} value={value || 'all'} onChange={onChange} />
    </div>
  )
}

const PARTS = [['Morning', 0, 720], ['Afternoon', 720, 1020], ['Evening', 1020, 1440]]
const shortDay = (iso) => {
  const l = dayLabel(iso)
  return /^(Today|Tomorrow)$/.test(l) ? l : moment(iso).format('ddd D')
}
const saidDay = (iso) => {
  const l = dayLabel(iso)
  return /^(Today|Tomorrow)$/.test(l) ? l.toLowerCase() : `on ${l}`
}
const listed = (list) => (list.length < 2 ? list.join('') : `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`)
const WORDS = ['None', 'One', 'Two', 'Three', 'Four']

// Free slots from the diary: whose free times (everyone's or one stylist's),
// a day with free time, then its free times to tap (they go on the post
// earliest first), or another day and a time by hand. `single` for one free
// slot.
const SlotPicker = ({ post, max = 3, single = false, onChange, onMore }) => {
  const c = post.content || {}
  const { days: all, failed } = useFreeDays()
  const { team, own, me, self, canChoose } = useWhose(all)
  const [openDate, setOpenDate] = useState(false)
  const [note, setNote] = useState(null)
  const anchor = useRef(null)
  const row = useRef(null)
  const limit = single ? 1 : max
  const chosen = single ? (c.time ? [String(c.time)] : []) : (c.times || []).filter(Boolean)
  const whoId = c.staffId ? String(c.staffId) : null
  const whoName = c.staffName || ((team.find(t => t.id === whoId) || {}).firstName) || ''
  const whose = whoId && whoId === me ? 'your' : whoName ? `${whoName}’s` : ''
  // A self-employed stylist's times go on as theirs
  const stamp = own && !whoId && self ? whoPatch(self) : {}
  const days = useMemo(() => (all ? daysFor(all, whoId) : null), [all, whoId])
  const day = (days || []).find(d => d.date === c.date) || null
  // The day's free times, plus any on the post that are free but between them
  // (1:30pm in a gap from 1pm)
  const between = day ? chosen.filter(t => freeAt(day.gaps, t) && !day.slots.some(s => minutesOf(s.label) === minutesOf(t))) : []
  const slots = day ? [...day.slots, ...between.map(t => ({ mins: minutesOf(t), label: t, staff: [] }))].sort((a, b) => a.mins - b.mins) : []
  const inRange = !!c.date && c.date >= isoIn(0) && c.date <= isoIn(7)
  const reads = c.date ? dayLabel(c.date, postingDay(post)) : ''
  // Times on the post that aren't free that day (or a day the diary wasn't read for)
  const extra = chosen.filter(t => !day || !freeAt(day.gaps, t))
  // All the design takes are on: the rest wait until one comes off
  const full = !single && chosen.length >= limit

  // On a phone the days scroll sideways: keep the chosen one in view
  useEffect(() => {
    const el = row.current
    const on = el && el.querySelector('.is-on')
    if (!on || el.scrollWidth <= el.clientWidth) return
    const box = el.getBoundingClientRect()
    const r = on.getBoundingClientRect()
    if (r.left < box.left || r.right > box.right) el.scrollLeft += r.left - box.left - (box.width - r.width) / 2
  }, [c.date, all, whoId])

  const write = (patch) => { setNote(null); onChange({ ...stamp, ...patch }) }
  // A day's times: spread through it, as many as usually fit
  const timesFor = (d) => {
    const picks = pickTimes(d.slots, single ? 1 : Math.min(limit, 3)).map(s => s.label)
    return single ? { time: picks[0] } : { times: picks, more: d.slots.length > picks.length }
  }
  const pickDay = (iso) => {
    hapticTick()
    const d = (days || []).find(x => x.date === iso)
    write(d ? { date: iso, ...timesFor(d) } : { date: iso })
  }
  // Someone else's free times: the day and times stay when they're free for
  // them too, else their free times that day, else their next free day
  const pickWho = (id) => {
    const member = id === 'all' ? null : team.find(t => t.id === id) || null
    if ((member ? member.id : null) === whoId) return
    hapticTick()
    const patch = whoPatch(member)
    if (!member || !all) { write(patch); return }
    const theirs = daysFor(all, member.id)
    const d = theirs.find(x => x.date === c.date)
    if (d) {
      const still = chosen.filter(t => freeAt(d.gaps, t))
      write({ ...patch, ...(still.length ? (single ? { time: still[0] } : { times: still }) : timesFor(d)) })
      return
    }
    const next = theirs.find(x => !c.date || x.date >= c.date) || theirs[0]
    if (!next) {
      write(patch)
      setNote(`${member.firstName} has nothing free in the next week.`)
      return
    }
    write({ ...patch, date: next.date, ...timesFor(next) })
    if (c.date) setNote(`${member.firstName} has nothing free ${saidDay(c.date)}, so the post is ${saidDay(next.date)} now.`)
  }
  const toggle = (label) => {
    hapticTick()
    if (single) { write({ time: label }); return }
    if (!hasTime(chosen, label) && chosen.length >= limit) { setNote(`${WORDS[limit] || limit} fit this design. Take one off first.`); return }
    write({ times: toggleTime(chosen, label, limit) })
  }
  const addOwn = (d) => {
    const label = timeLabel(d.getHours() * 60 + d.getMinutes())
    if (!hasTime(chosen, label)) toggle(label)
  }

  return (
    <>
      {canChoose && (
        <div className="sx-field" id="sx-f-who">
          <span className="sx-label">Whose free times</span>
          <WhosePicker team={team} me={me} value={whoId} extra={whoId ? { id: whoId, name: c.staffName || 'Someone' } : null} onChange={pickWho} />
          {!whoId && <span className="sx-hint">Everyone’s together, or pick someone to post just theirs.</span>}
        </div>
      )}
      {whoId && (
        <label className="sx-toggle" htmlFor="sx-f-showname">
          <input id="sx-f-showname" type="checkbox" checked={c.showName !== false} onChange={(e) => { const v = e.target.checked; hapticTick(); write({ showName: v }) }} />
          <span className="sx-toggle-track" aria-hidden><span /></span>
          <span className="sx-label">{whoId === me ? 'Show your name on the post' : whoName ? `Show ${whoName}’s name on the post` : 'Show their name on the post'}</span>
        </label>
      )}

      <div className="sx-field" id="sx-f-date">
        <span className="sx-label">Day</span>
        <div ref={row} className="sx-daychips" role="radiogroup" aria-label="Day">
          {days === null && [0, 1, 2].map(i => <span key={i} className="sx-daychip is-loading" aria-hidden />)}
          {(days || []).map(d => (
            <button key={d.date} type="button" role="radio" aria-checked={d.date === c.date} className={`sx-daychip${d.date === c.date ? ' is-on' : ''}`} onClick={() => pickDay(d.date)}>
              <strong>{shortDay(d.date)}</strong>
              <span>{d.slots.length} free</span>
            </button>
          ))}
          <button ref={anchor} type="button" className={`sx-daychip is-other${c.date && !day ? ' is-on' : ''}`} onClick={() => { hapticTick(); setOpenDate(true) }}>
            <CalendarIcon size={15} />
            <strong>{c.date && !day ? shortDay(c.date) : 'Another day'}</strong>
          </button>
        </div>
        <DiaryDatePicker
          open={openDate}
          selected={parseIso(c.date) || new Date()}
          anchorEl={anchor.current}
          onChange={(d) => {
            setOpenDate(false)
            if (moment(d).isBefore(moment(), 'day')) { setNote('That day has been and gone. Pick today or later.'); return }
            pickDay(isoDay(d))
          }}
          onClose={() => setOpenDate(false)}
        />
        <span className="sx-hint">{reads ? `Reads as “${reads}” on the day you post.` : 'Shown as Today, Tomorrow or the day’s name, on the day you post.'}</span>
      </div>

      <div className="sx-field" id={single ? 'sx-f-time' : 'sx-f-times'}>
        {single ? <span className="sx-label">Time</span> : (
          <div className="sx-slot-head">
            <span className="sx-label">Times</span>
            <span className={`sx-slot-count${full ? ' is-full' : ''}`} aria-live="polite">{chosen.length} of {limit} picked</span>
          </div>
        )}
        {/* The rule, and anything to say about the times, where the eye is: by the count */}
        {!single && (
          <span className={`sx-hint sx-slot-rule${note ? ' is-warn' : ''}`} role={note ? 'alert' : undefined}>
            {note || `Pick up to ${(WORDS[limit] || String(limit)).toLowerCase()}. That’s what fits this design.`}
          </span>
        )}
        {days === null && <div className="sx-slotchips" aria-hidden>{[0, 1, 2, 3].map(i => <span key={i} className="sx-slotchip is-loading" />)}</div>}
        {failed && <span className="sx-hint">Your diary couldn’t be read just now. Add the times below.</span>}
        {days && !failed && !days.length && whoId && !c.date && <span className="sx-hint">{whose ? `Nothing free in ${whose} diary in the next week.` : 'Nothing free in the next week.'}</span>}
        {days && !failed && c.date && !day && (
          <span className="sx-hint">{inRange ? (whose ? `Nothing free in ${whose} diary that day.` : 'Nothing free in your diary that day.') : 'Free times show for the next week. Add the times below.'}</span>
        )}
        {PARTS.map(([name, from, to]) => {
          const list = slots.filter(s => s.mins >= from && s.mins < to)
          if (!list.length) return null
          return (
            <div key={name} className="sx-slotpart">
              <span className="sx-slotpart-name">{name}</span>
              <div className="sx-slotchips">
                {list.map((s) => {
                  const on = hasTime(chosen, s.label)
                  const who = whoId ? [] : s.staff
                  const shut = full && !on
                  return (
                    <button
                      key={s.mins}
                      type="button"
                      aria-pressed={on}
                      className={`sx-slotchip${on ? ' is-on' : ''}${shut ? ' is-shut' : ''}`}
                      title={who.length ? `${listed(who)} free` : undefined}
                      aria-label={who.length ? `${s.label}, ${listed(who)} free` : s.label}
                      onClick={() => toggle(s.label)}
                    >
                      {s.label}
                    </button>
                  )
                })}
              </div>
            </div>
          )
        })}
        {extra.length > 0 && (
          <div className="sx-slotpart">
            <span className="sx-slotpart-name">{day ? (whoName && whoId !== me ? `Not free for ${whoName}` : 'Not free in your diary') : 'On the post'}</span>
            <div className="sx-slotchips">
              {extra.map(t => (
                <button key={t} type="button" className={`sx-slotchip is-on is-own${day ? ' is-warn' : ''}`} onClick={() => (single ? write({ time: '' }) : write({ times: chosen.filter(x => x !== t) }))} aria-label={`Take ${t} off`}>
                  {t}<CloseIcon size={12} />
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="sx-slot-add">
          <ShiftTimePicker label={single ? 'The time' : 'Add a time'} placeholder={single ? 'Pick another time' : 'Add another time'} selected={null} onChange={addOwn} />
        </div>
        <span className={`sx-hint${single && note ? ' is-warn' : ''}`} role={single && note ? 'alert' : undefined}>
          {(single && note) || (chosen.length ? `On the post${single || chosen.length < 2 ? '' : ', earliest first'}: ${listed(sortTimes(chosen))}.` : single ? 'Tap the time that’s free.' : 'Tap the times you want on the post.')}
        </span>
        {!single && onMore && day && !c.more && day.slots.length > chosen.length && chosen.length >= limit && (
          <button type="button" className="sx-link sx-slot-more" onClick={() => { hapticTick(); onMore() }}>
            More free that day. Say there are more times
          </button>
        )}
      </div>
    </>
  )
}

export default SlotPicker
